import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Card, Checkbox, Col, Input, Row, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { CALL_API } from "../../Common/api";
import { Tab } from "../../Reusablecomponents/Tab";
import FilterIcon from "../../assets/images/Filter-Icon.svg";
import { ReactComponent as GKPM } from "../../assets/images/GKPM.svg";
import { ReactComponent as GArrow } from "../../assets/images/GArrow.svg";
import { ReactComponent as Stationary } from "../../assets/images/stationary.svg";
import { ReactComponent as Moving } from "../../assets/images/Movement Status.svg";
import { ReactComponent as Idle } from "../../assets/images/idle.svg";
import { ReactComponent as MovingVehicle } from "../../assets/images/MovingVehicle.svg";
import { ReactComponent as StationaryVehicle } from "../../assets/images/StationaryVehicle.svg";
import { ReactComponent as IdleVehicle } from "../../assets/images/Idlevehicle.svg";
import { LiveTrackStyle } from "./LiveTrack";
import NexusLiveMap from "./NexusLiveMap";
import { deriveVehicleState } from "./nexusVehicleState";

// Live Tracking for accounts linked to TraKing (userInfo.NexusCompId).
// Same layout as the standard Live Tracking page, fed by TraKing's monitoring
// data via GET nexus/nexuslive (scoped to the caller's own company by the
// backend). TraKing has no battery / lock / signal data, so those parts of the
// standard design are left out.

const REFRESH_MS = 30000;

const parseCoord = (decimal, hex) => {
  const d = Number(decimal);
  if (decimal !== null && decimal !== undefined && decimal !== "" && !Number.isNaN(d)) {
    return d;
  }
  if (!hex) return null;
  const str = String(hex).trim().toUpperCase();
  const n = parseFloat(str.replace(/[NSEW]/g, ""));
  if (Number.isNaN(n)) return null;
  return /[SW]$/.test(str) ? -Math.abs(n) : Math.abs(n);
};

const normalize = (r) => {
  let lat = parseCoord(r.latitude_decimal, r.latitude_hex);
  let lng = parseCoord(r.longitude_decimal, r.longitude_hex);
  if (!lat || !lng) {
    lat = null;
    lng = null;
  }
  const state = deriveVehicleState(r);
  return {
    id: r.asset_id,
    name: r.asset_name || `Asset ${r.asset_id}`,
    state,
    online: state !== "offline",
    lat,
    lng,
    speed: Number(r.speed) || 0,
    heading: r.heading_degree != null ? Number(r.heading_degree) : null,
    lastPing: r.last_ping,
    // The duration belongs to the backend's own state, so only show it when
    // that state is the same one we display
    stateSeconds:
      r.current_state === state ? r.state_duration_seconds : null,
    odometer: r.odreading != null ? Number(r.odreading) : null,
    driverName: r.master_driver_name,
    driverPhone: r.master_driver_tel,
  };
};

const FILTERS = [
  { key: "Offline", test: (v) => !v.online },
  { key: "Online", test: (v) => v.online },
  { key: "Moving", test: (v) => v.state === "moving" },
  { key: "Stationary", test: (v) => v.state === "parked" },
  { key: "Idle", test: (v) => v.state === "idle" },
  { key: "NotPositioned", test: (v) => v.lat === null },
];

const vehicleIcon = (v) =>
  v.state === "moving" ? (
    <MovingVehicle className="IconSize" />
  ) : v.state === "idle" ? (
    <IdleVehicle className="IconSize" />
  ) : (
    <StationaryVehicle className="IconSize" />
  );

const stateIcon = (v) =>
  v.state === "moving" ? <Moving /> : v.state === "idle" ? <Idle /> : <Stationary />;

const NexusLiveTrack = () => {
  const userInfo = useSelector((state) => state?.authTracking?.userInfo);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterVisible, setFilterVisible] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [hoveredVehicle, setHoveredVehicle] = useState(null);
  const [selectedVehicleFromList, setSelectedVehicleFromList] = useState(null);
  const [vehicleSelectionCounter, setVehicleSelectionCounter] = useState(0);
  const [activeTab, setActiveTab] = useState("All");
  const initialSelectionDone = useRef(false);
  const filterRef = useRef(null);
  const filterToggleRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await CALL_API("GET", "nexus/nexuslive");
      setVehicles((data?.monitoringData || []).map(normalize));
    } catch (err) {
      console.error("[NexusLiveTrack] fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Every vehicle is shown on the map until the user unticks it
  useEffect(() => {
    if (initialSelectionDone.current || !vehicles.length) return;
    initialSelectionDone.current = true;
    setSelectedIds(vehicles.map((v) => v.id));
  }, [vehicles]);

  useEffect(() => {
    const onClickOutside = (e) => {
      const t = e.target;
      if (
        (filterRef.current && filterRef.current.contains(t)) ||
        (filterToggleRef.current && filterToggleRef.current.contains(t))
      ) {
        return;
      }
      setFilterVisible(false);
    };
    document.addEventListener("click", onClickOutside);
    return () => document.removeEventListener("click", onClickOutside);
  }, []);

  // Row-click highlight lasts 2 seconds, as on the standard page
  const [highlightedId, setHighlightedId] = useState(null);
  useEffect(() => {
    if (!hoveredVehicle) return undefined;
    const timer = setTimeout(() => {
      setHoveredVehicle(null);
      setHighlightedId(null);
    }, 2000);
    return () => clearTimeout(timer);
  }, [hoveredVehicle]);

  const counts = useMemo(() => {
    const c = { total: vehicles.length };
    FILTERS.forEach((f) => {
      c[f.key] = vehicles.filter(f.test).length;
    });
    return c;
  }, [vehicles]);

  const listed = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const active = FILTERS.find((f) => f.key === selectedFilters[0]);
    return vehicles.filter((v) => {
      if (active && !active.test(v)) return false;
      if (!q) return true;
      return (
        v.name.toLowerCase().includes(q) ||
        (v.driverName || "").toLowerCase().includes(q)
      );
    });
  }, [vehicles, searchTerm, selectedFilters]);

  const mapVehicles = useMemo(
    () => listed.filter((v) => selectedIds.includes(v.id)),
    [listed, selectedIds],
  );

  const toggleFilter = (key) =>
    setSelectedFilters((prev) => (prev.includes(key) ? [] : [key]));

  const allChecked = listed.length > 0 && listed.every((v) => selectedIds.includes(v.id));
  const someChecked = listed.some((v) => selectedIds.includes(v.id));

  const toggleAll = (checked) =>
    setSelectedIds((prev) => {
      const ids = listed.map((v) => v.id);
      return checked
        ? Array.from(new Set([...prev, ...ids]))
        : prev.filter((id) => !ids.includes(id));
    });

  const onVehicleCheck = (v, checked) => {
    setSelectedIds((prev) =>
      checked ? Array.from(new Set([...prev, v.id])) : prev.filter((id) => id !== v.id),
    );
    if (checked && v.lat !== null) {
      setSelectedVehicleFromList(v);
      setVehicleSelectionCounter((n) => n + 1);
    }
  };

  const onVehicleClick = (v) => {
    if (v.lat === null) return;
    setHoveredVehicle(v);
    setHighlightedId(v.id);
  };

  const content = (
    <div className="tab-content-wrapper">
      <div className="map_container_checkboxsubcontainer">
        <div>{counts.total} Total</div>
        <div className="mapbox_online_icon">{counts.Online} Online</div>
        <div>{counts.Offline} Offline</div>
        <div className="mapbox_online_icon">{counts.Moving} Moving</div>
      </div>
      <div className="map_container_hierarchy">
        {listed.length === 0 ? (
          <div className="no-results-message">
            {loading ? "Loading..." : "No results matching your query"}
          </div>
        ) : (
          <div>
            <div
              className="company-node bold"
              style={{ cursor: "pointer" }}
              onClick={() => setExpanded((p) => !p)}
            >
              <Checkbox
                checked={allChecked}
                indeterminate={someChecked && !allChecked}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleAll(e.target.checked);
                }}
              />
              <GKPM className="company-icon" />
              <span className="node-name">{userInfo?.COMPNAME || "Vehicles"}</span>
              <GArrow className={`arrow-icon ${expanded ? "expanded" : "collapsed"}`} />
            </div>

            {expanded && (
              <div className="vehicle-list" style={{ marginLeft: 30, cursor: "pointer" }}>
                {listed.map((v, index) => (
                  <div
                    key={v.id}
                    className="vehicle-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      onVehicleClick(v);
                    }}
                  >
                    <div className="vehicle-header">
                      <Checkbox
                        checked={selectedIds.includes(v.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          onVehicleCheck(v, e.target.checked);
                        }}
                      />
                      {vehicleIcon(v)}
                      <span
                        className="vehicle-name"
                        style={{ color: v.online ? "#34C759" : "#5F6D8C" }}
                      >
                        {v.name}
                      </span>
                      <div className="StatusWrapper">
                        <span
                          className="status-dot"
                          style={{ backgroundColor: v.online ? "#34C759" : "#B7B7B7" }}
                        />
                        <span
                          className="StatusTooltip"
                          style={{ color: v.online ? "green" : "gray" }}
                        >
                          {v.online ? "Online" : "Offline"}
                        </span>
                      </div>
                    </div>
                    <div className="vehicle-details">
                      <span>{stateIcon(v)}</span>
                      {v.state === "moving" && (
                        <>
                          <span className="separator">|</span>
                          <span>{Math.round(v.speed)} km/h</span>
                        </>
                      )}
                    </div>
                    {index !== listed.length - 1 && <hr className="vehicle-divider" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const items = [{ key: "All", label: "All", children: content }];

  return (
    <LiveTrackStyle>
      <Row>
        <Col style={{ position: "relative" }} xl={24} xxl={24}>
          <div>
            <Spin spinning={loading && !vehicles.length} tip="Loading...">
              <NexusLiveMap
                vehicles={mapVehicles}
                hoveredVehicle={hoveredVehicle}
                highlightedId={highlightedId}
                selectedVehicleFromList={selectedVehicleFromList}
                vehicleSelectionCounter={vehicleSelectionCounter}
              />
            </Spin>

            <div>
              <Card className="card_container" headStyle={{ display: "none" }}>
                <div className="container_three">
                  <div className="left-section">
                    <Input
                      placeholder="Search"
                      allowClear
                      className="search-input"
                      prefix={
                        <SearchOutlined
                          style={{ color: "rgba(0,0,0,.25)", fontSize: "20px" }}
                        />
                      }
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="right-section">
                    <Badge count={""}>
                      <div
                        className="Iconbg"
                        ref={filterToggleRef}
                        onClick={() => setFilterVisible((p) => !p)}
                      >
                        <img className="filter-icon-img" src={FilterIcon} alt="Filter Icon" />
                      </div>
                    </Badge>
                    {filterVisible && (
                      <div ref={filterRef} className="filter-popup">
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {FILTERS.map((f) => (
                            <div
                              key={f.key}
                              onClick={() => toggleFilter(f.key)}
                              className={`filter-option ${selectedFilters.includes(f.key) ? "active" : ""}`}
                            >
                              {f.key} ({counts[f.key] ?? 0})
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="custom_dashboard_ai">
                  <Tab
                    className="custom_dashboard_ai_tab"
                    items={items}
                    onChange={setActiveTab}
                    activeKey={activeTab}
                    defaultActiveKey="All"
                  />
                </div>
              </Card>
            </div>
          </div>
        </Col>
      </Row>
    </LiveTrackStyle>
  );
};

export default NexusLiveTrack;
